import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const Variables = () => {
    const [variables, setVariables] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    
    const [formData, setFormData] = useState({
        id: null,
        name: '',
        is_active: true,
        products: [],
        options: [] // Local state to manage options array
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [varRes, prodRes] = await Promise.all([
                api.get('/api/menu/variables/'),
                api.get('/api/menu/products/')
            ]);
            const varData = varRes.data.results || varRes.data;
            const prodData = prodRes.data.results || prodRes.data;
            setVariables(Array.isArray(varData) ? varData : []);
            setProducts(Array.isArray(prodData) ? prodData : []);
            setError(null);
        } catch (err) {
            console.error('Error fetching data:', err);
            setError('Error al cargar los datos');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handleProductToggle = (productId) => {
        setFormData(prev => {
            const products = [...prev.products];
            if (products.includes(productId)) {
                return { ...prev, products: products.filter(id => id !== productId) };
            } else {
                return { ...prev, products: [...products, productId] };
            }
        });
    };

    const addOptionRow = () => {
        setFormData({
            ...formData,
            options: [...formData.options, { id: null, name: '', extra_price: 0, is_active: true, is_deleted: false }]
        });
    };

    const handleOptionChange = (index, field, value) => {
        const newOptions = [...formData.options];
        newOptions[index][field] = value;
        setFormData({ ...formData, options: newOptions });
    };

    const removeOptionRow = (index) => {
        const newOptions = [...formData.options];
        if (newOptions[index].id) {
            // Mark for deletion on the server
            newOptions[index].is_deleted = true;
        } else {
            // Remove locally
            newOptions.splice(index, 1);
        }
        setFormData({ ...formData, options: newOptions });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            let variableId = formData.id;
            
            // 1. Save or Update Variable
            const variablePayload = {
                name: formData.name,
                is_active: formData.is_active,
                products: formData.products
            };
            
            if (variableId) {
                await api.put(`/api/menu/variables/${variableId}/`, variablePayload);
            } else {
                const res = await api.post('/api/menu/variables/', variablePayload);
                variableId = res.data.id;
            }

            // 2. Process Options separately because we didn't add nested write in Django
            for (const opt of formData.options) {
                if (opt.is_deleted && opt.id) {
                    await api.delete(`/api/menu/variable-options/${opt.id}/`);
                } else if (!opt.is_deleted) {
                    const optPayload = {
                        variable: variableId,
                        name: opt.name,
                        extra_price: opt.extra_price,
                        is_active: opt.is_active
                    };
                    if (opt.id) {
                        await api.put(`/api/menu/variable-options/${opt.id}/`, optPayload);
                    } else {
                        await api.post('/api/menu/variable-options/', optPayload);
                    }
                }
            }

            setShowModal(false);
            fetchData();
            resetForm();
        } catch (err) {
            console.error('Error saving variable:', err);
            alert('Error al guardar la variable');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('¿Está seguro de eliminar esta variable?')) {
            try {
                await api.delete(`/api/menu/variables/${id}/`);
                fetchData();
            } catch (err) {
                console.error('Error deleting variable:', err);
                alert('Error al eliminar la variable');
            }
        }
    };

    const editVariable = (variable) => {
        setFormData({
            id: variable.id,
            name: variable.name,
            is_active: variable.is_active,
            products: variable.products || [],
            options: variable.options ? variable.options.map(o => ({ ...o, is_deleted: false })) : []
        });
        setShowModal(true);
    };

    const resetForm = () => {
        setFormData({
            id: null,
            name: '',
            is_active: true,
            products: [],
            options: []
        });
    };

    if (loading) return <div style={{ padding: '2rem' }}>Cargando variables...</div>;
    if (error) return <div style={{ padding: '2rem', color: 'red' }}>{error}</div>;

    return (
        <div style={{ padding: '2rem', maxWidth: '900px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <h2 style={{ margin: 0, color: 'var(--primary-color)' }}>Gestión de Variables (Sabores/Tipos)</h2>
                <button 
                    onClick={() => { resetForm(); setShowModal(true); }}
                    style={{
                        padding: '0.75rem 1.5rem',
                        backgroundColor: 'var(--primary-color)',
                        color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer',
                        fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '0.5rem'
                    }}
                >
                    <i className="bi bi-plus-circle"></i> Nueva Variable
                </button>
            </div>

            <div style={{ backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                            <th style={{ padding: '1rem', textAlign: 'left', color: '#374151' }}>Variable</th>
                            <th style={{ padding: '1rem', textAlign: 'left', color: '#374151' }}>Opciones</th>
                            <th style={{ padding: '1rem', textAlign: 'center', color: '#374151' }}>Estado</th>
                            <th style={{ padding: '1rem', textAlign: 'right', color: '#374151' }}>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {variables.map(v => (
                            <tr key={v.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                <td style={{ padding: '1rem', fontWeight: '500' }}>{v.name}</td>
                                <td style={{ padding: '1rem' }}>
                                    {v.options?.filter(o => o.is_active).map(o => o.name).join(', ') || <span style={{color: '#9ca3af'}}>Sin opciones</span>}
                                </td>
                                <td style={{ padding: '1rem', textAlign: 'center' }}>
                                    <span style={{ 
                                        padding: '0.25rem 0.75rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 'bold',
                                        backgroundColor: v.is_active ? '#d1fae5' : '#fee2e2',
                                        color: v.is_active ? '#065f46' : '#991b1b'
                                    }}>
                                        {v.is_active ? 'Activa' : 'Inactiva'}
                                    </span>
                                </td>
                                <td style={{ padding: '1rem', textAlign: 'right' }}>
                                    <button onClick={() => editVariable(v)} style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', marginRight: '1rem' }}>
                                        <i className="bi bi-pencil-square" style={{ fontSize: '1.25rem' }}></i>
                                    </button>
                                    <button onClick={() => handleDelete(v.id)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}>
                                        <i className="bi bi-trash" style={{ fontSize: '1.25rem' }}></i>
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {variables.length === 0 && (
                            <tr>
                                <td colSpan="4" style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                                    No hay variables creadas. Por ejemplo, crea una variable "Chocolates" y ponle opciones "Blanco" y "Negro".
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Modal */}
            {showModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: '#fff', borderRadius: '12px', padding: '2rem',
                        width: '100%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto',
                        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
                    }}>
                        <h3 style={{ marginTop: 0, marginBottom: '1.5rem', color: '#111827' }}>
                            {formData.id ? 'Editar Variable' : 'Nueva Variable'}
                        </h3>
                        <form onSubmit={handleSubmit}>
                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#374151', fontWeight: '500' }}>Nombre de la Variable (Ej. Tipo de Chocolate)</label>
                                <input 
                                    type="text" name="name" value={formData.name} onChange={handleInputChange} required
                                    style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #d1d5db', outline: 'none' }}
                                />
                            </div>
                            <div style={{ marginBottom: '1.5rem' }}>
                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                    <input 
                                        type="checkbox" name="is_active" checked={formData.is_active} onChange={handleInputChange}
                                        style={{ marginRight: '0.5rem', width: '1rem', height: '1rem' }}
                                    />
                                    <span style={{ color: '#374151', fontWeight: '500' }}>Variable Activa</span>
                                </label>
                            </div>

                            {/* Opciones */}
                            <div style={{ marginBottom: '1.5rem', border: '1px solid #e5e7eb', padding: '1rem', borderRadius: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                    <label style={{ color: '#374151', fontWeight: 'bold' }}>Opciones (Ej. Blanco, Negro)</label>
                                    <button type="button" onClick={addOptionRow} style={{
                                        background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer'
                                    }}>+ Añadir Opción</button>
                                </div>
                                {formData.options.map((opt, index) => {
                                    if (opt.is_deleted) return null;
                                    return (
                                        <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                                            <input type="text" placeholder="Nombre (Ej. Blanco)" value={opt.name} onChange={(e) => handleOptionChange(index, 'name', e.target.value)} required style={{ flex: 1, padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px' }} />
                                            <input type="number" step="0.01" placeholder="Precio Extra" value={opt.extra_price} onChange={(e) => handleOptionChange(index, 'extra_price', e.target.value)} style={{ width: '100px', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px' }} />
                                            <button type="button" onClick={() => removeOptionRow(index)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '1.25rem' }}><i className="bi bi-x-circle-fill"></i></button>
                                        </div>
                                    )
                                })}
                                {formData.options.filter(o => !o.is_deleted).length === 0 && <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>Agrega opciones a esta variable</span>}
                            </div>

                            {/* Productos Aplicables */}
                            <div style={{ marginBottom: '1.5rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#374151', fontWeight: 'bold' }}>Aplica a los productos:</label>
                                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                    <select 
                                        id="productSelect"
                                        style={{ flex: 1, padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
                                        defaultValue=""
                                    >
                                        <option value="" disabled>Seleccione un producto...</option>
                                        {products.filter(p => !formData.products.includes(p.id)).map(p => (
                                            <option key={p.id} value={p.id}>{p.name}</option>
                                        ))}
                                    </select>
                                    <button 
                                        type="button" 
                                        onClick={() => {
                                            const selectEl = document.getElementById('productSelect');
                                            if (selectEl.value) {
                                                const prodId = selectEl.value; // product ID is a UUID string
                                                if (!formData.products.includes(prodId)) {
                                                    setFormData({...formData, products: [...formData.products, prodId]});
                                                    selectEl.value = "";
                                                }
                                            }
                                        }}
                                        style={{ background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '4px', padding: '0 1rem', cursor: 'pointer', fontWeight: 'bold' }}
                                    >
                                        Agregar
                                    </button>
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', maxHeight: '150px', overflowY: 'auto', padding: '0.5rem', border: '1px solid #e5e7eb', borderRadius: '8px', minHeight: '50px' }}>
                                    {formData.products.length === 0 && <span style={{ color: '#9ca3af', fontSize: '0.875rem', fontStyle: 'italic' }}>Ningún producto agregado todavía.</span>}
                                    {formData.products.map(prodId => {
                                        const p = products.find(prod => prod.id === prodId);
                                        if (!p) return null;
                                        return (
                                            <div key={prodId} style={{ display: 'flex', alignItems: 'center', backgroundColor: '#e5e7eb', padding: '0.25rem 0.5rem', borderRadius: '999px', fontSize: '0.8125rem', fontWeight: '500' }}>
                                                <span>{p.name}</span>
                                                <button type="button" onClick={() => {
                                                    setFormData({...formData, products: formData.products.filter(id => id !== prodId)});
                                                }} style={{ background: 'none', border: 'none', color: '#ef4444', marginLeft: '0.25rem', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center' }}>
                                                    &times;
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                                <button type="button" onClick={() => setShowModal(false)} style={{ padding: '0.75rem 1.5rem', backgroundColor: '#e5e7eb', color: '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}>Cancelar</button>
                                <button type="submit" style={{ padding: '0.75rem 1.5rem', backgroundColor: 'var(--primary-color)', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}>Guardar</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Variables;
